import Seo from "../../components/Seo";
import { Reveal, Eyebrow, PageHero, CtaBand } from "../../components/Shared";
import { BUSINESS } from "../../data/site";
import { Phone, Home as HomeIcon, Building2, Sun, Thermometer, Eye, Lamp } from "lucide-react";
import { Link } from "react-router-dom";

export default function ResComTint() {
  return (
    <>
      <Seo
        title="Residential & Commercial Window Tinting in Sioux Falls, SD | Free Quote — A+ Detailing"
        description="Residential and commercial window tinting in Sioux Falls, SD — heat rejection, glare reduction, UV protection and privacy for homes and businesses. Free quote from A+ Detailing."
        path="/residential-commercial-tinting"
      />
      <PageHero
        eyebrow="Residential & Commercial Tinting"
        lines={["TINT FOR", "HOMES &", "BUSINESSES."]}
        lead="GeoShield architectural window film for your home or commercial flat glass — a different class of film than automotive tint. Cooler summers, less glare, added privacy and protection from fading, quoted free for your exact windows."
        chips={["Free quote", "Residential & commercial", "Sioux Falls, SD"]}
        testPrefix="rescom"
      />

      <section className="mx-auto max-w-7xl px-5 sm:px-8 py-20 sm:py-28">
        <div className="grid gap-5 lg:grid-cols-2">
          {[
            {
              icon: HomeIcon,
              t: "Residential",
              d: "Living rooms, sunrooms, west-facing windows and glass doors — keep your home cooler and your furnishings protected from sun fade.",
            },
            {
              icon: Building2,
              t: "Commercial",
              d: "Storefronts, offices and shared workspaces — cut glare on screens, lower interior heat and give your glass a clean, uniform look.",
            },
          ].map((c, i) => (
            <Reveal key={c.t} delay={i * 0.1}>
              <div className="h-full rounded-2xl border border-white/10 bg-card p-9" data-testid={`rescom-type-${i}`}>
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-bright/10 border border-bright/20 text-bright">
                  <c.icon size={22} />
                </span>
                <h2 className="mt-5 font-display text-2xl font-bold">{c.t}</h2>
                <p className="mt-3 text-sm text-steel leading-relaxed">{c.d}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal><Eyebrow className="mt-20">A look at tinted glass</Eyebrow></Reveal>
        <div className="mt-8">
          <Reveal>
            <div className="relative overflow-hidden rounded-2xl border border-white/10" data-testid="rescom-photo-home">
              <img src="https://static.prod-images.emergentagent.com/jobs/367a57ed-9ef4-4e83-9ace-080c6cd7bce4/images/c73c55c8ad488597dac63504b08363e52a5e779d8be61534d98611385887e6cb.jpeg" alt="Modern home with professionally tinted windows at dusk" className="w-full h-[320px] lg:h-[460px] object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/80 via-transparent to-transparent" />
              <div className="absolute bottom-5 left-6 sm:left-8">
                <p className="font-display text-xl sm:text-2xl font-bold">Cooler summers. Protected interiors.</p>
              </div>
            </div>
          </Reveal>
          <div className="grid gap-4 mt-4 md:grid-cols-2">
            <Reveal delay={0.08}>
              <div className="relative overflow-hidden rounded-2xl border border-white/10" data-testid="rescom-photo-living">
                <img src="https://static.prod-images.emergentagent.com/jobs/367a57ed-9ef4-4e83-9ace-080c6cd7bce4/images/4c76a35ca3716ce2fcc90bcb57ac5c7aae18092d1688ab46fd85761bbf71ff26.jpeg" alt="Living room with large tinted floor-to-ceiling windows" className="w-full h-[240px] lg:h-[300px] object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
                <p className="absolute bottom-4 left-5 font-display text-lg font-bold">Glare-free living spaces</p>
              </div>
            </Reveal>
            <Reveal delay={0.16}>
              <div className="relative overflow-hidden rounded-2xl border border-white/10" data-testid="rescom-photo-commercial">
                <img src="https://static.prod-images.emergentagent.com/jobs/367a57ed-9ef4-4e83-9ace-080c6cd7bce4/images/fd96f4c034317869911a8a1a523f44ac5b21e8a718279aecfca4db19bab71e87.jpeg" alt="Commercial glass building with dark tinted windows" className="w-full h-[240px] lg:h-[300px] object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
                <p className="absolute bottom-4 left-5 font-display text-lg font-bold">Storefronts & offices</p>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <p className="mt-4 text-[11px] text-muted">Illustrative imagery of tinted glass installations.</p>
          </Reveal>
        </div>

        <Reveal><Eyebrow className="mt-20">What film does for your glass</Eyebrow></Reveal>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: Thermometer, t: "Heat rejection", d: "Keep interiors noticeably cooler through the summer months." },
            { icon: Sun, t: "UV protection", d: "Help shield skin, flooring, furniture and merchandise from sun exposure." },
            { icon: Lamp, t: "Glare reduction", d: "Comfortable screens, displays and workspaces without the harsh sun." },
            { icon: Eye, t: "Privacy", d: "Added privacy for homes, offices and storefront glass — day and night options." },
          ].map((b, i) => (
            <Reveal key={b.t} delay={i * 0.08}>
              <div className="h-full rounded-2xl border border-white/10 bg-card p-7" data-testid={`rescom-benefit-${i}`}>
                <b.icon size={22} className="text-bright" />
                <h3 className="mt-4 font-display text-lg font-bold">{b.t}</h3>
                <p className="mt-2 text-sm text-steel leading-relaxed">{b.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24">
        <Reveal>
          <div className="relative overflow-hidden rounded-2xl border border-white/10" data-testid="rescom-photo-install">
            <img src="https://static.prod-images.emergentagent.com/jobs/367a57ed-9ef4-4e83-9ace-080c6cd7bce4/images/fb147e8ef883ba5ca0329bb979beb5cf32e81c3f9b8da1bc1a97b39d46f69435.jpeg" alt="A+ Detailing installer applying window tint film to a large residential living room window" className="w-full h-[300px] lg:h-[420px] object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/50 to-transparent" />
            <div className="absolute inset-0 flex items-center">
              <div className="px-7 sm:px-12 max-w-md">
                <Eyebrow>Clean, professional installs</Eyebrow>
                <p className="mt-2 font-display text-2xl sm:text-3xl font-extrabold">Precision-fit film on every pane.</p>
              </div>
            </div>
          </div>
        </Reveal>
      </section>

      <section className="bg-coal border-y border-white/5 py-20 sm:py-28">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <Reveal>
            <div className="rounded-2xl border border-bright/40 bg-gradient-to-br from-bright/10 to-card p-9 sm:p-12 text-center" data-testid="rescom-quote-card">
              <h2 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Free tint quote</h2>
              <p className="mt-4 text-steel leading-relaxed">
                Every home and building is different — film choice and window count set
                the price. Call or send a quote request and we'll measure it out with you.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3.5 justify-center">
                <a
                  href={BUSINESS.phoneHref}
                  data-testid="rescom-call-button"
                  className="flex items-center justify-center gap-2 rounded-full bg-bright hover:bg-accent text-white font-semibold px-7 py-3.5 transition-colors"
                >
                  <Phone size={16} /> Call {BUSINESS.phone}
                </a>
                <Link
                  to="/book"
                  data-testid="rescom-quote-button"
                  className="flex items-center justify-center gap-2 rounded-full border border-white/20 hover:border-bright hover:text-bright text-white font-semibold px-7 py-3.5 transition-colors"
                >
                  Request a Free Quote
                </Link>
              </div>
              <p className="mt-6 text-xs text-muted">
                We tint vehicles too —{" "}
                <Link to="/window-tinting" className="text-bright hover:text-white transition-colors">automotive window tinting</Link>
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 sm:px-8 pb-24">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <div>
            <Reveal><Eyebrow>The film we install</Eyebrow></Reveal>
            <Reveal delay={0.08}>
              <h2 className="mt-4 font-display text-3xl sm:text-4xl font-extrabold tracking-tight">GeoShield flat glass films</h2>
            </Reveal>
            <Reveal delay={0.14}>
              <p className="mt-4 max-w-xl text-sm text-steel leading-relaxed">
                Purpose-built architectural film for homes and commercial buildings — solar control, privacy and safety lines, all blocking 99% of UV rays.
              </p>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <img src="/geoshield-logo.svg" alt="GeoShield window film logo" className="h-14 w-auto" loading="lazy" />
          </Reveal>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[
            { name: "Geo", specs: "99% UV · 47–60% TSER", note: "Lighter shades (30–50% VLT) for a bright, comfortable interior" },
            { name: "Lunar", specs: "99% UV · 62% TSER", note: "Strong solar control in a 20% VLT" },
            { name: "Astro", specs: "99% UV · 85% TSER", note: "Maximum heat rejection for tough exposures" },
            { name: "Ultra", specs: "99% UV · 49% TSER · 70% VLT", note: "Near-clear film with real heat rejection" },
            { name: "Solar Bronze", specs: "99% UV · 67% TSER", note: "Warm bronze tone with solid solar control" },
            { name: "Blackout, Frost & Safety", specs: "99% UV", note: "Blackout privacy, white frost decorative, and 8-mil clear safety film" },
          ].map((f, i) => (
            <Reveal key={f.name} delay={i * 0.06}>
              <div className="h-full rounded-2xl border border-white/10 bg-card p-7" data-testid={`rescom-film-${i}`}>
                <h3 className="font-display text-xl font-bold">{f.name}</h3>
                <p className="mt-3 font-mono text-xs text-bright">{f.specs}</p>
                <p className="mt-2 text-sm text-steel">{f.note}</p>
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <p className="mt-6 text-xs text-muted">
            Film specs per GeoShield's published data. We'll recommend the right film and shade for your windows during your free quote.
          </p>
        </Reveal>
      </section>

      <CtaBand testPrefix="rescom" />
    </>
  );
}
